import React from "react";
import { View, StyleSheet } from "react-native";
import BaseController from "../../common/base/BaseController";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtPageSection from "../../components/NtPageSection";
import NtText from "../../components/NtText";
import NtModal from "../../components/NtModal";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtThemePicker from "../../components/NtThemePicker";
import NtTabController from "../../components/NtTabController";
import NtUserUpdateForm from "../../user/components/NtUserUpdateForm";
import NtSettingsDevicesCard from "../components/NtSettingsDevicesCard";
import { fetchUser } from "../../api/user";
import { showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import { getAuthService as getPbxAuthService } from "@ntconnect/framework/pbx";
import { useTheme } from "../../context/ThemeContext";
import settings from "../../config/settings";
import packageJson from "../../../package.json";
import {
  PAGE_SIZE_OPTIONS,
  LANDING_ROUTE_OPTIONS,
  getPreferences,
  setPageSize,
  setLandingRoute,
} from "../../config/preferences";

// Hook child so the class controller can read theme tokens inside the page.
function ProfileCard({ user, profile, onEdit }) {
  const { theme } = useTheme();
  const styles = makeProfileStyles(theme);
  const name =
    profile?.contact?.contact_name_given ||
    user?.displayName ||
    user?.username ||
    "Account";
  const email = profile?.user_email || profile?.email || user?.email || "";
  const username = user?.username;
  const initials = getInitials(name);
  return (
    <View style={styles.card}>
      <View style={styles.avatar}>
        <NtText style={styles.avatarText}>{initials}</NtText>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <NtText style={styles.name} numberOfLines={1}>
          {name}
        </NtText>
        {email ? (
          <NtText style={styles.meta} numberOfLines={1}>
            {email}
          </NtText>
        ) : null}
        {username && username !== name ? (
          <NtText style={styles.meta} numberOfLines={1}>
            @{username}
          </NtText>
        ) : null}
      </View>
      <NtTouchableEffect style={styles.editBtn} onPress={onEdit}>
        <NtMaterialIcon name="pencil" color={theme.brand2} size={14} />
        <NtText style={styles.editText}>Edit profile</NtText>
      </NtTouchableEffect>
    </View>
  );
}

// Display preferences — rows-per-page + default landing page. Persisted via
// the preferences module; rows-per-page is mirrored into settings.pageSizeLoad
// so existing list controllers pick it up without code changes.
function DisplayPreferences() {
  const { theme } = useTheme();
  const prefs = getPreferences();
  const [pageSize, setPageSizeState] = React.useState(prefs.pageSize);
  const [landing, setLandingState] = React.useState(prefs.landingRoute);

  const pageSizeOptions = PAGE_SIZE_OPTIONS.map((n) => ({
    id: String(n),
    title: String(n),
  }));
  const pageSizeSelection = pageSizeOptions.find(
    (o) => o.id === String(pageSize),
  );
  const landingSelection = LANDING_ROUTE_OPTIONS.find((o) => o.id === landing);

  return (
    <View>
      <View style={prefRowStyle}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <NtText style={{ fontSize: 13, fontWeight: "600", color: theme.text }}>
            Rows per page
          </NtText>
          <NtText style={{ fontSize: 11, marginTop: 2, color: theme.textDim }}>
            Default page size for every list (Extensions, Ring Groups, IVR, etc.).
          </NtText>
        </View>
        <NtTabController
          options={pageSizeOptions}
          selection={pageSizeSelection}
          onPress={(item) => {
            const n = parseInt(item.id, 10);
            setPageSizeState(n);
            setPageSize(n);
          }}
        />
      </View>
      <View
        style={{
          ...prefRowStyle,
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: theme.borderSoft,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <NtText style={{ fontSize: 13, fontWeight: "600", color: theme.text }}>
            Default landing page
          </NtText>
          <NtText style={{ fontSize: 11, marginTop: 2, color: theme.textDim }}>
            Where you land right after signing in.
          </NtText>
        </View>
        <NtTabController
          options={LANDING_ROUTE_OPTIONS}
          selection={landingSelection}
          onPress={(item) => {
            setLandingState(item.id);
            setLandingRoute(item.id);
          }}
        />
      </View>
    </View>
  );
}

// About — version, build, backend, identity. Static-ish read-only block,
// useful for support tickets.
function AboutCard({ user }) {
  const { theme } = useTheme();
  const rowStyle = {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 10,
  };
  const dividerStyle = {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.borderSoft,
  };
  const lab = {
    color: theme.textMute,
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.4,
  };
  const val = {
    color: theme.text,
    fontSize: 12,
    fontWeight: "600",
  };
  const mono = { fontFamily: theme.fontMono || "Menlo" };

  const rows = [
    { label: "VERSION", value: packageJson.version, mono: true },
    { label: "ENVIRONMENT", value: deriveEnvironmentLabel() },
    { label: "BACKEND", value: settings.apiUrl, mono: true },
    { label: "ALIAS", value: user?.domain || "—" },
    { label: "USER ID", value: user?.userId || "—", mono: true },
  ];

  return (
    <View>
      {rows.map((row, idx) => (
        <View
          key={row.label}
          style={[rowStyle, idx > 0 && dividerStyle]}
        >
          <NtText style={lab}>{row.label}</NtText>
          <NtText
            style={[val, row.mono && mono, { maxWidth: "60%", textAlign: "right" }]}
            numberOfLines={1}
          >
            {row.value}
          </NtText>
        </View>
      ))}
    </View>
  );
}

function deriveEnvironmentLabel() {
  // Detect where the app itself is running (page host), not where the
  // backend lives — those can differ (e.g. localhost dev session hitting
  // the production API).
  const host =
    typeof window !== "undefined" && window.location
      ? window.location.hostname
      : "";
  if (!host) return "Unknown";
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local"))
    return "Local";
  if (host.startsWith("qa.") || host.startsWith("staging.") || host.includes("-staging"))
    return "Staging / QA";
  return "Production";
}

const prefRowStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
  paddingHorizontal: 14,
  paddingVertical: 12,
};

// Hook child for the Session section so the sign-out row can read theme.
function SessionRow({ onSignOutPress }) {
  const { theme } = useTheme();
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
      }}
    >
      <View style={{ flex: 1 }}>
        <NtText style={{ fontSize: 13, fontWeight: "600", color: theme.text }}>
          Sign out
        </NtText>
        <NtText style={{ fontSize: 11, marginTop: 2, color: theme.textDim }}>
          End your session and return to the sign-in screen.
        </NtText>
      </View>
      <NtModalActiveButton
        title="Sign out"
        icon="logout"
        containerStyle={{ backgroundColor: theme.red }}
        onPress={onSignOutPress}
        enabled={true}
      />
    </View>
  );
}

const makeProfileStyles = (t) =>
  StyleSheet.create({
    card: {
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      padding: 14,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: t.brand,
      alignItems: "center",
      justifyContent: "center",
    },
    avatarText: {
      color: "#ffffff",
      fontSize: 16,
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    name: { color: t.text, fontSize: 15, fontWeight: "700", letterSpacing: -0.2 },
    meta: { color: t.textDim, fontSize: 12, marginTop: 2 },
    editBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: t.radius,
      borderWidth: 1,
      borderColor: t.borderSoft,
      backgroundColor: t.surface2,
    },
    editText: { color: t.text, fontSize: 12, fontWeight: "600" },
  });

class SettingsController extends BaseController {
  state = {
    ...super.state,
    profileRecord: null,
  };

  editModalRef = React.createRef();
  signOutModalRef = React.createRef();

  componentDidMount() {
    super.componentDidMount?.();
    this.handleProfileFetch();
  }

  handleProfileFetch = async () => {
    const user = this.context?.user;
    if (!user?.domain || !user?.userId) return;
    const res = await fetchUser(user.domain, user.userId);
    Logger("Settings · fetchUser", res);
    if (res?.ok) {
      const raw = res.data?.data || res.data || {};
      this.setState({ profileRecord: raw });
    }
  };

  handleEditProfile = () => this.editModalRef.current?.show();

  handleProfileUpdated = () => {
    this.editModalRef.current?.dismiss();
    this.handleProfileFetch();
    showToast("Success", "Profile updated");
  };

  handleSignOut = async () => {
    this.signOutModalRef.current?.dismiss();
    try {
      // Best-effort BFF logout; also clears local tokens server-side has
      // already revoked. Network/server errors are swallowed inside logout().
      await getPbxAuthService().logout();
    } catch (err) {
      Logger("BFF logout failed (continuing local sign-out)", err);
    }
    this.context.updateUser(null);
  };

  render() {
    const user = this.context?.user;
    const profile = this.state.profileRecord;
    return (
      <NtRootPageContainer
        pageTitle="Settings"
        pageSubtitle="Manage your profile, appearance, and session."
        permission={this.handlePermissionCheck()}
      >
        <View style={styles.column}>
          <NtPageSection title="Profile">
            <ProfileCard
              user={user}
              profile={profile}
              onEdit={this.handleEditProfile}
            />
          </NtPageSection>

          <NtPageSection title="Appearance">
            <View style={styles.body}>
              <NtThemePicker />
            </View>
          </NtPageSection>

          <NtPageSection title="Display preferences">
            <DisplayPreferences />
          </NtPageSection>

          <NtPageSection title="About">
            <AboutCard user={user} />
          </NtPageSection>

          <NtPageSection title="Devices">
            <NtSettingsDevicesCard />
          </NtPageSection>

          <NtPageSection title="Session">
            <SessionRow
              onSignOutPress={() => this.signOutModalRef.current?.show()}
            />
          </NtPageSection>
        </View>

        <NtModal ref={this.editModalRef}>
          {profile ? (
            <NtUserUpdateForm
              item={profile}
              onCancel={() => this.editModalRef.current?.dismiss()}
              onUpdate={this.handleProfileUpdated}
            />
          ) : null}
        </NtModal>

        <NtModal ref={this.signOutModalRef}>
          <NtModalConfirmAlert
            title="Sign out"
            message="Are you sure you want to sign out?"
            onCancel={() => this.signOutModalRef.current?.dismiss()}
            onAccept={this.handleSignOut}
          />
        </NtModal>
      </NtRootPageContainer>
    );
  }
}

function getInitials(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/[\s.@_-]+/);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase() || first.toUpperCase();
}

const styles = StyleSheet.create({
  column: { gap: 16 },
  body: { paddingHorizontal: 14, paddingVertical: 14 },
});

export default SettingsController;
